import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { streamAPI } from '../services/api';
import { config, getVideoUrl, getImageUrl } from '../config';
import InteractPanel from './interact/InteractPanel';
import useDualTrackPlayback from './playback/useDualTrackPlayback';

const FEED_SPECIAL_CATEGORIES = ['intro', 'idle'];
// /stream/:id 是登录态预览页，不走 Feed 的 public_only 公开过滤。
const STREAM_PREVIEW_REQUEST = { publicOnly: false };
const clampVolume = (value, fallback = 100) => {
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed)) return fallback;
  return Math.max(0, Math.min(100, parsed));
};
const warmImage = (src) => {
  if (!src || typeof Image === 'undefined') return;
  const image = new Image();
  image.src = src;
};

/**
 * StreamPage - 直播间页面
 */
const StreamPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // 核心状态
  const [stream, setStream] = useState(null);
  const [videos, setVideos] = useState([]);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [baseVideo, setBaseVideo] = useState(null);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [isShowingInitial, setIsShowingInitial] = useState(true);
  const [showPosterWhileLoading, setShowPosterWhileLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeButton, setActiveButton] = useState(null);
  const [playbackPhase, setPlaybackPhase] = useState('idle');
  const [isRealtimeGenerating, setIsRealtimeGenerating] = useState(false);
  const [realtimeGenMessage, setRealtimeGenMessage] = useState('');
  const realtimePollRef = useRef(null);
  
  // 音频控制
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [videoVolume, setVideoVolume] = useState(80);
  const [currentMusic, setCurrentMusic] = useState(null);
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const [musicVolume, setMusicVolume] = useState(10);
  const musicAudioRef = useRef(null);
  
  // UI 状态
  const [showSettings, setShowSettings] = useState(false);
  const [showInteractPanel, setShowInteractPanel] = useState(false);
  
  // 社交统计（模拟数据）
  const [viewerCount, setViewerCount] = useState(() => {
    const seed = parseInt(id) || 1;
    const baseCount = Math.abs(Math.floor((Math.sin(seed * 0.3) * 20000) % 20000));
    return Math.max(1000, baseCount + Math.floor(Math.random() * 30000));
  });
  const [likeCount, setLikeCount] = useState(177);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followerCount] = useState(() => {
    const seed = parseInt(id) || 1;
    const baseCount = Math.abs(Math.floor((Math.sin(seed * 0.1) * 1000) % 1000));
    return Math.max(baseCount, 100) + Math.floor(Math.random() * 500);
  });
  
  // 亲密互动生成状态
  const [isGeneratingIntimate, setIsGeneratingIntimate] = useState(false);
  const [intimateProgress, setIntimateProgress] = useState(0);
  const [intimateMessage, setIntimateMessage] = useState('');
  const [rotatingVideos, setRotatingVideos] = useState([]);
  const [currentRotatingIndex, setCurrentRotatingIndex] = useState(0);
  const [completedIntimateAction, setCompletedIntimateAction] = useState(null);
  
  // Refs
  const isRequesting = useRef(false);
  const intimatePollIntervalRef = useRef(null);
  const singleVideoRef = useRef(null);
  
  // 左滑返回手势状态
  const [swipeHintActive, setSwipeHintActive] = useState(false);
  const [swipeTriggered, setSwipeTriggered] = useState(false);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const isSwiping = useRef(false);
  
  // 模拟定时器（用 ref 存储，避免引发 re-render）
  const viewerIntervalRef = useRef(null);
  const likeIntervalRef = useRef(null);
  
  // 热点映射功能
  const [hotspots, setHotspots] = useState([]);
  const [showHotspots, setShowHotspots] = useState(true); // 默认启用（隐形热点）
  
  // 拖拽状态
  const [dragState, setDragState] = useState({
    isDragging: false,
    startX: 0,
    startY: 0,
    currentX: 0,
    currentY: 0,
    hotspotId: null
  });
  
  // 双击和长按状态
  const [clickState, setClickState] = useState({
    lastClickTime: 0,
    lastClickHotspotId: null,
    longPressTimer: null,
    isLongPressing: false
  });

  // 获取直播间数据
  const fetchStreamData = useCallback(async () => {
    if (isRequesting.current) return;
    
    try {
      isRequesting.current = true;
      setLoading(true);
      setError(null);
      if (realtimePollRef.current) {
        clearInterval(realtimePollRef.current);
        realtimePollRef.current = null;
      }
      setIsRealtimeGenerating(false);
      setRealtimeGenMessage('');

      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('请求超时')), 30000);
      });
      
      const [streamResponse, videosResponse] = await Promise.race([
        Promise.all([
          streamAPI.getStream(id, STREAM_PREVIEW_REQUEST),
          streamAPI.getStreamVideos(id, STREAM_PREVIEW_REQUEST)
        ]),
        timeoutPromise
      ]);
      
      setStream(streamResponse.data);
      const list = videosResponse.data || [];
      setVideos(list);

      const introVideo = list.find(v => v.video_category === 'intro');
      const idleVideo = list.find(v => v.video_category === 'idle');
      const resolvedBase = idleVideo || streamResponse.data.default_video || null;
      setBaseVideo(resolvedBase);

      if (introVideo) {
        setCurrentVideo(introVideo);
        setIsVideoReady(false);
        setPlaybackPhase('intro');
        setIsShowingInitial(false);
        setShowPosterWhileLoading(true);
      } else if (resolvedBase) {
        setCurrentVideo(resolvedBase);
        setIsVideoReady(false);
        setPlaybackPhase('idle');
        setIsShowingInitial(false);
        setShowPosterWhileLoading(true);
      } else {
        setCurrentVideo(null);
        setIsVideoReady(false);
        setPlaybackPhase('idle');
        setIsShowingInitial(true);
        setShowPosterWhileLoading(false);
      }
    } catch (err) {
      setError(`获取直播间数据失败: ${err.message}`);
    } finally {
      setLoading(false);
      isRequesting.current = false;
    }
  }, [id]);

  // 启动模拟效果（观众数、点赞数变化）
  const startSimulation = useCallback(() => {
    viewerIntervalRef.current = setInterval(() => {
      setViewerCount(prev => {
        const maxChange = Math.max(10, Math.floor(prev * 0.02));
        const change = Math.floor(Math.random() * maxChange * 2) - maxChange;
        return Math.max(500, prev + change);
      });
    }, 3000 + Math.random() * 2000);

    likeIntervalRef.current = setInterval(() => {
      if (Math.random() < 0.3) {
        setLikeCount(prev => prev + Math.floor(Math.random() * 3) + 1);
      }
    }, 5000 + Math.random() * 5000);
  }, []);

  /**
   * 停止模拟效果
   */
  const stopSimulation = useCallback(() => {
    if (viewerIntervalRef.current) {
      clearInterval(viewerIntervalRef.current);
      viewerIntervalRef.current = null;
    }
    if (likeIntervalRef.current) {
      clearInterval(likeIntervalRef.current);
      likeIntervalRef.current = null;
    }
  }, []);

  // useEffect Hook - 当直播间ID变化时重新获取数据
  useEffect(() => {
    fetchStreamData();
  }, [id, fetchStreamData]);

  useEffect(() => {
    if (currentVideo) {
      setIsVideoReady(false);
      setShowPosterWhileLoading(true);
      return;
    }

    setIsVideoReady(false);
    setShowPosterWhileLoading(false);
  }, [currentVideo?.id, currentVideo?.filename, currentVideo]);

  const currentPosterUrl = currentVideo?.first_frame_url
    || stream?.initial_image_url
    || stream?.character_image_url
    || null;
  const currentVideoSrc = currentVideo
    ? (currentVideo.video_url || getVideoUrl(currentVideo.filename))
    : null;

  useEffect(() => {
    if (currentPosterUrl) {
      warmImage(currentPosterUrl);
    }
  }, [currentPosterUrl]);

  // 加载热点配置
  useEffect(() => {
    fetch('/demo/hotspot-mapping.json')
      .then(res => res.json())
      .then(data => {
        const config = data.streams[id];
        if (config && config.hotspots) {
          // 处理新格式：将 interactions 数组转换为前端兼容格式
          const processedHotspots = config.hotspots.map(hotspot => {
            if (hotspot.interactions && Array.isArray(hotspot.interactions)) {
              // 新格式：使用明确的 action_type 字段
              const processed = { ...hotspot };
              
              hotspot.interactions.forEach(interaction => {
                if (interaction.action_type === 'click') {
                  if (interaction.candidates.length === 1) {
                    processed.interaction_type = interaction.candidates[0];
                  } else {
                    processed.interaction_types = interaction.candidates;
                  }
                } else if (interaction.action_type === 'doubleclick') {
                  if (interaction.candidates.length === 1) {
                    processed.doubleclick_type = interaction.candidates[0];
                  } else {
                    processed.doubleclick_types = interaction.candidates;
                  }
                } else if (interaction.action_type === 'longpress') {
                  if (interaction.candidates.length === 1) {
                    processed.longpress_type = interaction.candidates[0];
                  } else {
                    processed.longpress_types = interaction.candidates;
                  }
                  if (interaction.duration) {
                    processed.longpress_duration = interaction.duration;
                  }
                }
              });
              
              return processed;
            }
            
            // 旧格式，直接返回
            return hotspot;
          });
          
          setHotspots(processedHotspots);
        }
      })
      .catch(() => {});
  }, [id]);

  // 监听快捷键：按 H 键开启/关闭热点显示
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'h' || e.key === 'H') {
        setShowHotspots(prev => !prev);
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  // useEffect Hook - 清理亲密互动与实时生成轮询
  useEffect(() => {
    return () => {
      if (intimatePollIntervalRef.current) {
        clearInterval(intimatePollIntervalRef.current);
      }
      if (realtimePollRef.current) {
        clearInterval(realtimePollRef.current);
        realtimePollRef.current = null;
      }
    };
  }, []); // 依赖数组包含id和相关函数，当id变化时重新执行

  // 当前视频切换时应用后台配置的默认音量与 BGM。
  useEffect(() => {
    if (!currentVideo) return;
    setVideoVolume(clampVolume(currentVideo.video_volume, 100));
    setMusicVolume(clampVolume(currentVideo.background_music_volume, 30));
  }, [
    currentVideo,
    currentVideo?.id,
    currentVideo?.background_music_volume,
    currentVideo?.video_volume,
  ]);

  // 空间级 BGM 初始化：只在 BGM 源发生变化时切换，避免视频切换时跳播。
  useEffect(() => {
    const streamMusicId = stream?.background_music_id ?? currentVideo?.background_music_id;
    const streamMusicUrl = stream?.background_music_audio_url || currentVideo?.background_music_audio_url;
    const bgmEnabled = Number(
      currentVideo?.background_music_enabled
      ?? (streamMusicId ? 1 : 0)
    ) === 1;
    if (bgmEnabled && streamMusicId && streamMusicUrl) {
      setCurrentMusic((prev) => {
        if (prev?.id === streamMusicId && prev?.audio_url === streamMusicUrl && prev?.source === 'stream') {
          return prev;
        }
        return {
          id: streamMusicId,
          title: stream?.background_music_title || currentVideo?.background_music_title || '背景音乐',
          filename: stream?.background_music_filename || currentVideo?.background_music_filename,
          audio_url: streamMusicUrl,
          source: 'stream',
        };
      });
      setIsMusicPlaying(true);
      return;
    }

    setIsMusicPlaying(false);
    setCurrentMusic(null);
  }, [
    currentVideo?.background_music_audio_url,
    currentVideo?.background_music_enabled,
    currentVideo?.background_music_id,
    currentVideo?.background_music_title,
    currentVideo?.background_music_filename,
    stream?.background_music_audio_url,
    stream?.background_music_filename,
    stream?.background_music_id,
    stream?.background_music_title,
  ]);

  // 启动模拟效果的useEffect
  useEffect(() => {
    if (stream) {
      startSimulation();
    }
    
    // 清理函数
    return () => {
      stopSimulation();
    };
  }, [stream, startSimulation, stopSimulation]);

  // 左滑返回手势处理
  const handleTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;
    isSwiping.current = false;
    
    // 只在屏幕左侧 1/4 区域开始的滑动才触发返回
    if (touch.clientX < window.innerWidth * 0.25) {
      isSwiping.current = true;
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isSwiping.current) return;
    
    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartX.current;
    const deltaY = touch.clientY - touchStartY.current;
    
    // 确保是水平滑动（水平移动大于垂直移动）
    if (Math.abs(deltaX) > Math.abs(deltaY) && deltaX > 0) {
      // 显示滑动提示
      if (deltaX > 20) {
        setSwipeHintActive(true);
      }
      
      // 滑动超过阈值，标记为触发
      if (deltaX > 100) {
        setSwipeTriggered(true);
      } else {
        setSwipeTriggered(false);
      }
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (swipeTriggered) {
      navigate('/streams');
    }
    
    // 重置状态
    setSwipeHintActive(false);
    setSwipeTriggered(false);
    isSwiping.current = false;
  }, [swipeTriggered, navigate]);

  // 添加触摸事件监听
  useEffect(() => {
    const container = document.querySelector('.stream-layout');
    if (!container) return;
    
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: true });
    container.addEventListener('touchend', handleTouchEnd);
    
    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  const activeMusicSrc = isMusicPlaying ? (currentMusic?.audio_url || null) : null;
  const {
    isVideoReady: dualTrackReady,
    isAutoplayFallbackMuted,
    attemptPlayback,
    restartLoopAlignment,
    stopAll,
  } = useDualTrackPlayback({
    videoRef: singleVideoRef,
    audioRef: musicAudioRef,
    videoSrc: currentVideoSrc,
    audioSrc: activeMusicSrc,
    active: Boolean(currentVideoSrc) && !isShowingInitial,
    shouldPlay: Boolean(currentVideoSrc) && !isShowingInitial,
    muted: isMuted,
    videoVolume,
    audioVolume: musicVolume,
    reuseAudio: true,
    onReady: () => {
      setShowPosterWhileLoading(false);
    },
    onPlaybackError: () => {
      setIsVideoReady(false);
      setShowPosterWhileLoading(true);
    },
  });

  const effectiveMuted = isMuted || isAutoplayFallbackMuted;

  useEffect(() => {
    setIsVideoReady(dualTrackReady);
  }, [dualTrackReady]);

  useEffect(() => {
    if (!currentVideoSrc || isShowingInitial) {
      stopAll({ resetAudio: true });
    }
  }, [currentVideoSrc, isShowingInitial, stopAll]);

  /**
   * 判断直播间状态
   * @returns {Object} 包含状态信息的对象
   */
  const getStreamStatus = () => {
    if (!stream) {
      return { status: 'loading', label: '加载中...', isLive: false, isResting: false };
    }

    if (stream.is_active === 0) {
      return { status: 'inactive', label: '准备中', isLive: false, isResting: false };
    }

    const hasIdle = videos.some(v => v.video_category === 'idle');
    const hasDefault = !!stream.default_video;
    const hasAnyPlayable = videos.length > 0;

    if (hasDefault || hasIdle || hasAnyPlayable) {
      return { status: 'live', label: '互动中', isLive: true, isResting: false };
    }

    return { status: 'resting', label: '休息中', isLive: false, isResting: true };
  };

  /**
   * 开始亲密互动生成流程
   * @param {string} taskId - 生成任务ID
   */
  const handleIntimateGenerationStart = (taskId) => {
    setIsGeneratingIntimate(true);
    setIntimateProgress(0);
    setIntimateMessage('正在准备生成...');

    const publicVideos = videos.filter(v =>
      !FEED_SPECIAL_CATEGORIES.includes(v.video_category) &&
      v.video_category !== 'private'
    );
    if (publicVideos.length > 0) {
      const shuffled = [...publicVideos].sort(() => 0.5 - Math.random());
      const selected = shuffled.slice(0, Math.min(2, publicVideos.length));
      setRotatingVideos(selected);
      setCurrentRotatingIndex(0);

      if (selected.length > 0) {
        setCurrentVideo(selected[0]);
        setIsShowingInitial(false);
        setShowPosterWhileLoading(true);
      }
    }

    pollIntimateTaskStatus(taskId);
  };

  /**
   * 轮询亲密互动生成任务状态
   * @param {string} taskId - 任务ID
   */
  const pollIntimateTaskStatus = (taskId) => {
    // 清除之前的轮询
    if (intimatePollIntervalRef.current) {
      clearInterval(intimatePollIntervalRef.current);
    }
    
    let failureCount = 0; // 连续失败计数
    const MAX_FAILURES = 5; // 最大连续失败次数
    
    intimatePollIntervalRef.current = setInterval(async () => {
      try {
        const response = await fetch(`/streams/${id}/interaction-status/${taskId}`);
        
        if (!response.ok) {
          throw new Error('查询任务状态失败');
        }
        
        const status = await response.json();
        
        // 重置失败计数（查询成功）
        failureCount = 0;
        
        setIntimateProgress(status.progress || 0);
        setIntimateMessage(status.message || '生成中...');
        
        if (status.status === 'completed') {
          // 生成完成
          clearInterval(intimatePollIntervalRef.current);
          intimatePollIntervalRef.current = null;
          setIntimateMessage('生成完成！');
          
          const newVideoId = status.video_id;
          
          // 延迟1秒后清除生成状态，刷新视频列表
          setTimeout(async () => {
            setIsGeneratingIntimate(false);
            setRotatingVideos([]);
            
            // 刷新视频列表
            let allVideos = [];
            try {
              const videosResponse = await streamAPI.getStreamVideos(id, STREAM_PREVIEW_REQUEST);
              allVideos = videosResponse.data || [];
              setVideos(allVideos);
            } catch { /* ignore */ }
            
            // 查找新视频
            const newVideo = allVideos.find(v => v.id === newVideoId);
            
            if (newVideo) {
              // 显示完成提示
              setCompletedIntimateAction({
                label: newVideo.interaction_label || '专属动作',
                videoId: newVideo.id
              });
              
              // 30秒后自动清除完成提示
              setTimeout(() => {
                setCompletedIntimateAction(null);
              }, 30000);
              
              // 直接切换到新视频（简化版：直接设置 currentVideo）
              setIsShowingInitial(false);
              setCurrentVideo(newVideo);
              setPlaybackPhase('action');
              setShowPosterWhileLoading(true);
            }
          }, 1000);
        } else if (status.status === 'failed') {
          // 生成失败
          clearInterval(intimatePollIntervalRef.current);
          intimatePollIntervalRef.current = null;
          setIntimateProgress(0);
          setIntimateMessage(`❌ 生成失败: ${status.error || '未知错误'}`);
          
          // 延迟5秒后清除生成状态并恢复默认视频
          setTimeout(() => {
            setIsGeneratingIntimate(false);
            setRotatingVideos([]);
            // 恢复到默认视频
            if (baseVideo) {
              setCurrentVideo(baseVideo);
              setPlaybackPhase('idle');
              setShowPosterWhileLoading(true);
            }
          }, 5000);
        }
      } catch {
        failureCount++;
        
        // 如果连续失败次数超过阈值，停止轮询并显示错误
        if (failureCount >= MAX_FAILURES) {
          clearInterval(intimatePollIntervalRef.current);
          intimatePollIntervalRef.current = null;
          
          setIntimateProgress(0);
          setIntimateMessage('❌ 网络错误，无法获取生成状态');
          
          // 延迟5秒后清除生成状态并恢复默认视频
          setTimeout(() => {
            setIsGeneratingIntimate(false);
            setRotatingVideos([]);
            // 恢复到默认视频
            if (baseVideo) {
              setCurrentVideo(baseVideo);
              setPlaybackPhase('idle');
              setShowPosterWhileLoading(true);
            }
          }, 5000);
        }
      }
    }, 2000); // 每2秒查询一次
  };

  // 处理用户点击互动按钮（预制立即切换 / 实时生成走接口）
  const handleInteraction = async (interactionId) => {
    const targetVideo = videos.find(v => v.id === interactionId);
    if (!targetVideo) return;
    if (isRealtimeGenerating) return;

    setIsMuted(false);
    void tryPlayCurrentVideo({ preferSound: true });
    setActiveButton(targetVideo.interaction_type);
    playSound(targetVideo.interaction_type);
    setCompletedIntimateAction(null);

    if (targetVideo.generation_mode === 'realtime') {
      setIsRealtimeGenerating(true);
      setRealtimeGenMessage('正在提交...');
      if (realtimePollRef.current) {
        clearInterval(realtimePollRef.current);
        realtimePollRef.current = null;
      }
      try {
        const formData = new FormData();
        formData.append('interaction_prompt', targetVideo.video_prompt || targetVideo.interaction_type || '');
        const res = await fetch(`${config.API_BASE_URL}/streams/${id}/add-interaction`, {
          method: 'POST',
          body: formData
        });
        const data = await res.json();
        if (!res.ok) {
          const d = data.detail;
          throw new Error(typeof d === 'string' ? d : (d?.message || JSON.stringify(d) || '提交失败'));
        }
        const taskId = data.task_id;
        setRealtimeGenMessage('正在生成视频...');
        realtimePollRef.current = setInterval(async () => {
          try {
            const statusRes = await fetch(`${config.API_BASE_URL}/streams/${id}/interaction-status/${taskId}`);
            const statusData = await statusRes.json();
            if (!statusRes.ok) return;
            setRealtimeGenMessage(statusData.message || '生成中...');
            if (statusData.status === 'completed' && statusData.video) {
              if (realtimePollRef.current) {
                clearInterval(realtimePollRef.current);
                realtimePollRef.current = null;
              }
              setIsRealtimeGenerating(false);
              const nv = {
                ...statusData.video,
                stream_id: parseInt(id, 10),
                generation_mode: 'preset',
                is_candidate: 0
              };
              setVideos(prev => (prev.some(v => v.id === nv.id) ? prev : [...prev, nv]));
              setCurrentVideo(nv);
              setPlaybackPhase('action');
              setIsShowingInitial(false);
              setShowPosterWhileLoading(true);
            }
            if (statusData.status === 'failed') {
              if (realtimePollRef.current) {
                clearInterval(realtimePollRef.current);
                realtimePollRef.current = null;
              }
              setIsRealtimeGenerating(false);
              setRealtimeGenMessage(statusData.error || '生成失败');
            }
          } catch { /* ignore poll errors */ }
        }, 2500);
      } catch (err) {
        setIsRealtimeGenerating(false);
        setRealtimeGenMessage(err.message || '提交失败');
      }
      setTimeout(() => setActiveButton(null), 3000);
      return;
    }

    setPlaybackPhase('action');
    setCurrentVideo(targetVideo);
    setIsShowingInitial(false);
    setShowPosterWhileLoading(true);
    setTimeout(() => setActiveButton(null), 3000);
  };

  // 通用函数：根据interaction_type(s)查找并播放视频
  const findAndPlayVideo = (types, typesArray, actionName) => {
    let targetVideo = null;
    
    // 支持两种配置方式
    if (typesArray && Array.isArray(typesArray) && typesArray.length > 0) {
      // 方式1: 多个候选，随机选择
      const availableVideos = videos.filter(v => typesArray.includes(v.interaction_type));
      if (availableVideos.length > 0) {
        targetVideo = availableVideos[Math.floor(Math.random() * availableVideos.length)];
      }
    } else if (types) {
      targetVideo = videos.find(v => v.interaction_type === types);
    }
    
    // 触发视频播放
    if (targetVideo) {
      handleInteraction(targetVideo.id);
      return true;
    }
    return false;
  };

  // 处理热点点击 - 支持单个或多个候选interaction_type
  const handleHotspotClick = (hotspot) => {
    findAndPlayVideo(
      hotspot.interaction_type, 
      hotspot.interaction_types, 
      `单击热点: ${hotspot.label}`
    );
  };

  // 处理热点双击
  const handleHotspotDoubleClick = (hotspot) => {
    const played = findAndPlayVideo(
      hotspot.doubleclick_type, 
      hotspot.doubleclick_types, 
      `双击热点: ${hotspot.label}`
    );
    
  };

  const handleHotspotLongPress = (hotspot) => {
    const played = findAndPlayVideo(
      hotspot.longpress_type, 
      hotspot.longpress_types, 
      `长按热点: ${hotspot.label}`
    );
    
  };

  // 处理鼠标按下（开始检测长按和拖拽）
  const handleHotspotMouseDown = (e, hotspot) => {
    e.stopPropagation();
    
    // 如果是拖拽模式，启动拖拽
    if (hotspot.interaction_mode === 'drag') {
      handleDragStart(e, hotspot);
      return;
    }
    
    // 检查是否配置了长按
    const hasLongPress = hotspot.longpress_type || (hotspot.longpress_types && hotspot.longpress_types.length > 0);
    
    if (hasLongPress) {
      // 启动长按计时器（默认800ms）
      const duration = hotspot.longpress_duration || 800;
      const timer = setTimeout(() => {
        setClickState(prev => ({ ...prev, isLongPressing: true }));
        handleHotspotLongPress(hotspot);
      }, duration);
      
      setClickState(prev => ({
        ...prev,
        longPressTimer: timer,
        isLongPressing: false
      }));
    }
  };

  // 处理鼠标抬起（触发单击或双击）
  const handleHotspotMouseUp = (e, hotspot) => {
    e.stopPropagation();
    
    // 如果是拖拽模式，结束拖拽
    if (hotspot.interaction_mode === 'drag') {
      handleDragEnd(e, hotspot);
      return;
    }
    
    // 清除长按计时器
    if (clickState.longPressTimer) {
      clearTimeout(clickState.longPressTimer);
    }
    
    // 如果已经触发了长按，不再触发点击
    if (clickState.isLongPressing) {
      setClickState(prev => ({
        ...prev,
        longPressTimer: null,
        isLongPressing: false
      }));
      return;
    }
    
    // 检测双击
    const now = Date.now();
    const timeSinceLastClick = now - clickState.lastClickTime;
    const DOUBLE_CLICK_THRESHOLD = 300; // 300ms内的两次点击视为双击
    
    // 检查是否配置了双击
    const hasDoubleClick = hotspot.doubleclick_type || (hotspot.doubleclick_types && hotspot.doubleclick_types.length > 0);
    
    if (hasDoubleClick && 
        timeSinceLastClick < DOUBLE_CLICK_THRESHOLD && 
        clickState.lastClickHotspotId === hotspot.id) {
      // 双击
      handleHotspotDoubleClick(hotspot);
      setClickState(prev => ({
        ...prev,
        lastClickTime: 0,
        lastClickHotspotId: null,
        longPressTimer: null
      }));
    } else {
      // 单击（或第一次点击）
      if (hasDoubleClick) {
        // 如果配置了双击，等待一小段时间再触发单击，以防是双击的第一次点击
        setTimeout(() => {
          const timeSinceThisClick = Date.now() - now;
          if (timeSinceThisClick >= DOUBLE_CLICK_THRESHOLD) {
            // 确认是单击
            handleHotspotClick(hotspot);
          }
        }, DOUBLE_CLICK_THRESHOLD);
      } else {
        // 没有配置双击，立即触发单击
        handleHotspotClick(hotspot);
      }
      
      setClickState(prev => ({
        ...prev,
        lastClickTime: now,
        lastClickHotspotId: hotspot.id,
        longPressTimer: null
      }));
    }
  };

  // 处理鼠标移出（取消长按）
  const handleHotspotMouseLeave = () => {
    if (clickState.longPressTimer) {
      clearTimeout(clickState.longPressTimer);
      setClickState(prev => ({
        ...prev,
        longPressTimer: null,
        isLongPressing: false
      }));
    }
  };

  // 处理拖拽开始
  const handleDragStart = (e, hotspot) => {
    if (hotspot.interaction_mode !== 'drag') return;
    
    e.preventDefault();
    e.stopPropagation();
    
    setDragState({
      isDragging: true,
      startX: e.clientX,
      startY: e.clientY,
      currentX: e.clientX,
      currentY: e.clientY,
      hotspotId: hotspot.id
    });
    
  };

  // 处理拖拽移动
  const handleDragMove = (e) => {
    if (!dragState.isDragging) return;
    
    setDragState(prev => ({
      ...prev,
      currentX: e.clientX,
      currentY: e.clientY
    }));
  };

  // 处理拖拽结束
  const handleDragEnd = (e, hotspot) => {
    if (!dragState.isDragging || dragState.hotspotId !== hotspot.id) return;
    
    const deltaX = dragState.currentX - dragState.startX;
    const deltaY = dragState.currentY - dragState.startY;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    
    // 判断拖动方向
    let direction = null;
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      direction = deltaX > 0 ? 'right' : 'left';
    } else {
      direction = deltaY > 0 ? 'down' : 'up';
    }
    
    // 检查是否达到最小距离
    const minDistance = hotspot.drag_config?.min_distance || 30;
    
    if (distance >= minDistance) {
      // 获取对应方向的动作
      const interactionType = hotspot.drag_config?.interaction_types?.[direction];
      
      if (interactionType) {
        const targetVideo = videos.find(v => v.interaction_type === interactionType);
        if (targetVideo) handleInteraction(targetVideo.id);
      }
    }
    
    // 重置拖拽状态
    setDragState({
      isDragging: false,
      startX: 0,
      startY: 0,
      currentX: 0,
      currentY: 0,
      hotspotId: null
    });
  };

  // 获取拖拽方向图标
  const getDragDirectionIcon = () => {
    if (!dragState.isDragging) return '';
    
    const deltaX = dragState.currentX - dragState.startX;
    const deltaY = dragState.currentY - dragState.startY;
    
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      return deltaX > 0 ? '→' : '←';
    } else {
      return deltaY > 0 ? '↓' : '↑';
    }
  };

  /**
   * 获取初始画面的完整URL
   */
  const getInitialImageUrl = () => {
    if (stream && (stream.initial_image_url || stream.initial_image)) {
      return stream.initial_image_url || getImageUrl(stream.initial_image);
    }
    return null;
  };

  /**
   * 播放音效
   * @param {string} soundType - 音效类型
   */
  const playSound = (soundType) => {
    if (!soundEnabled) return;
    
    try {
      // 创建音效上下文
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // 根据互动类型生成不同的音效
      const frequency = getFrequencyForInteraction(soundType);
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      // 音效播放失败，静默处理
    }
  };

  /**
   * 根据互动类型获取音效频率
   * @param {string} interactionType - 互动类型
   * @returns {number} - 音效频率
   */
  const getFrequencyForInteraction = (interactionType) => {
    const frequencyMap = {
      'wave': 440,    // A4
      'smile': 523,   // C5
      'dance': 659,   // E5
      'nod': 392,     // G4
      'clap': 784,    // G5
      'bow': 330,     // E4
      '握手': 494,    // B4
      '摸头': 622,    // D#5
      '变装': 740     // F#5
    };
    return frequencyMap[interactionType] || 440;
  };

  /**
   * 视频音频控制函数
   */

  const tryPlayCurrentVideo = useCallback(async ({ preferSound = true } = {}) => {
    const played = await attemptPlayback({ preferSound });
    return played;
  }, [attemptPlayback]);
  
  /**
   * 切换视频静音状态
   */
  const toggleVideoMute = () => {
    const nextMuted = !isMuted;
    setIsMuted(nextMuted);
    if (!nextMuted) {
      void tryPlayCurrentVideo({ preferSound: true });
    }
  };

  /**
   * 设置视频音量
   * @param {number} volume - 音量值 (0-100)
   */
  const setVideoVolumeLevel = (volume) => {
    const clampedVolume = Math.max(0, Math.min(100, volume));
    setVideoVolume(clampedVolume);
    if (clampedVolume === 0) {
      setIsMuted(true);
    } else if (isMuted) {
      setIsMuted(false);
      void tryPlayCurrentVideo({ preferSound: true });
    }
  };

  /**
   * 用户交互启用音频播放
   */
  const enableAudioPlayback = async () => {
    setIsMuted(false);
    await tryPlayCurrentVideo({ preferSound: true });
  };

  const toggleBackgroundMusic = () => {
    if (!currentMusic?.audio_url) {
      return;
    }
    setIsMusicPlaying(prev => !prev);
  };


  /**
   * 处理点赞
   */
  const handleLike = () => {
    setLikeCount(prev => prev + 1);
    playSound('clap');
  };

  /**
   * 处理关注/取消关注（简化版本，仅本地状态切换）
   */
  const handleFollow = () => {
    if (!stream) return;
    
    // 简化：仅切换本地状态，不调用后端API
    setIsFollowing(!isFollowing);
  };

  /**
   * 格式化计数显示（如：1.2k, 3.5w）
   */
  const formatCount = (count) => {
    if (count >= 10000) {
      return (count / 10000).toFixed(1) + 'w';
    } else if (count >= 1000) {
      return (count / 1000).toFixed(1) + 'k';
    }
    return count.toString();
  };

  // 条件渲染 - 加载状态
  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <h2>加载中...</h2>
        </div>
      </div>
    );
  }

  // 条件渲染 - 错误状态或直播间不存在
  if (error || (!stream && !loading)) {
    return (
      <div className="App">
        <div className="main-layout">
          <nav className="sidebar-nav">
            <div className="nav-logo">
              <span className="logo-icon">🤖</span>
            </div>
            <div className="nav-menu">
              <a href="/" className="nav-item" title="首页">
                <span className="nav-icon">🏠</span>
              </a>
              <a href="/create" className="nav-item" title="创建互动空间">
                <span className="nav-icon">➕</span>
              </a>

            </div>
          </nav>
          <div className="content-area">
            <div className="container">
              <div className="error">
                <h2>获取互动空间数据失败</h2>
                <p>{error || '互动空间不存在或数据加载失败'}</p>
                <button className="btn btn-primary" onClick={() => navigate('/')}>
                  返回首页
                </button>
                <button className="btn btn-secondary" onClick={() => window.location.reload()}>
                  重新加载
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 主页面渲染
  return (
    <div className="App stream-page">
      <div className="main-layout">
        {/* 左滑返回提示 - 移动端显示 */}
        <div className={`swipe-back-hint ${swipeHintActive ? 'active' : ''} ${swipeTriggered ? 'triggered' : ''}`}>
          <span className="swipe-back-arrow">←</span>
        </div>
        
        {/* 左边缘指示器 - 移动端显示 */}
        <div className="swipe-edge-indicator" />
        
        {/* 左侧导航栏 - 桌面端hover显示，移动端隐藏 */}
        <div className="sidebar-trigger-area" />
        <nav className="sidebar-nav stream-sidebar">
          <div className="nav-logo">
            <span className="logo-icon">🤖</span>
          </div>
          <div className="nav-menu">
            <a href="/" className="nav-item" title="首页">
              <span className="nav-icon">🏠</span>
            </a>
            <a href="/create" className="nav-item" title="创建互动空间">
              <span className="nav-icon">➕</span>
            </a>
          </div>
        </nav>

        <div className="content-area">
          {/* 右侧设置按钮 */}
          <div 
            className="settings-trigger-btn"
            onClick={() => setShowSettings(true)}
            title="设置"
          >
            ⚙️
          </div>

          {/* 右侧设置面板 */}
          <div className={`settings-panel-right ${showSettings ? 'open' : ''}`}>
            <div className="settings-header">
              <h3>设置</h3>
              <button className="close-btn" onClick={() => setShowSettings(false)}>×</button>
            </div>
            
            <div className="settings-content">
              {/* 视频音频设置 */}
              <div className="settings-group">
                <div className="group-title">🔊 视频音频</div>
                <div className="setting-item">
                  <span className="label">静音</span>
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={isMuted}
                      onChange={toggleVideoMute}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>
                <div className="setting-item column">
                  <span className="label">音量 ({videoVolume}%)</span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={videoVolume}
                    onChange={(e) => setVideoVolumeLevel(parseInt(e.target.value))}
                    className="volume-slider"
                  />
                </div>
                <button className="action-btn" onClick={enableAudioPlayback}>
                  🎵 启用音频
                </button>
              </div>

              {/* 背景音乐设置 */}
              <div className="settings-group">
                <div className="group-title">🎵 背景音乐</div>
                <div className="setting-item">
                  <span className="label">播放音乐</span>
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={isMusicPlaying}
                      onChange={toggleBackgroundMusic}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>
                <div className="setting-item column">
                  <span className="label">音量 ({musicVolume}%)</span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={musicVolume}
                    onChange={(e) => setMusicVolume(parseInt(e.target.value))}
                    className="volume-slider"
                  />
                </div>
                {currentMusic && (
                  <div className="current-music-info-small">
                    <div className="scrolling-text">{currentMusic.title}</div>
                  </div>
                )}
                {currentMusic && (
                  <div className="music-controls-row">
                    <button onClick={toggleBackgroundMusic}>{isMusicPlaying ? '⏸️' : '▶️'}</button>
                  </div>
                )}
              </div>

              {/* 其他设置 */}
              <div className="settings-group">
                <div className="group-title">🔔 其他</div>
                <div className="setting-item">
                  <span className="label">互动音效</span>
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={soundEnabled}
                      onChange={(e) => setSoundEnabled(e.target.checked)}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* 直播间布局 - 视频播放器和互动面板 */}
          <div className="stream-layout">
          {/* 视频播放区域 - (3) 点击视频区域关闭互动面板 */}
          <div 
            className="video-container"
            onClick={() => {
              if (showInteractPanel) {
                setShowInteractPanel(false);
              }
            }}
          >
            {/* 顶部账号信息栏 */}
            <div className="top-account-bar">
              <div className="account-info-left">
                <div className="account-name-top">{stream?.name || '未知AI伙伴'}</div>
                <div className="account-meta">
                  <span className="follower-count-top">{followerCount} 粉丝</span>
                  <button 
                    className={`follow-btn-top ${isFollowing ? 'following' : ''}`}
                    onClick={handleFollow}
                  >
                    {isFollowing ? '✓' : '+'}
                  </button>
                </div>
              </div>
            </div>

            {/* 右上角统计信息 */}
            <div className="top-right-stats">
              <div className="viewer-count-top">
                <span className="viewer-icon">👁️</span>
                <span className="viewer-number" key={viewerCount}>{viewerCount.toLocaleString()}</span>
              </div>
              <div className="like-count-top" onClick={handleLike}>
                <span className="like-icon">❤️</span>
                <span className="like-number">{likeCount}</span>
              </div>
            </div>

            {/* 生成亲密互动提示条 */}
            {isGeneratingIntimate && (() => {
              const isError = intimateMessage.includes('❌');
              return (
                <div style={{
                  position: 'absolute',
                  top: '80px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: isError ? 'rgba(220, 50, 50, 0.85)' : 'rgba(40, 40, 50, 0.7)',
                  backdropFilter: 'blur(15px)',
                  borderRadius: '16px',
                  padding: '16px 24px',
                  boxShadow: isError ? '0 8px 32px rgba(220, 50, 50, 0.4)' : '0 8px 32px rgba(0, 0, 0, 0.3)',
                  zIndex: 15,
                  minWidth: '320px',
                  maxWidth: '90%',
                  border: isError ? '1px solid rgba(255, 100, 100, 0.6)' : '1px solid rgba(255, 255, 255, 0.15)'
                }}>
                  {isError ? (
                    // 错误状态
                    <div style={{
                      color: 'white',
                      fontSize: '15px',
                      fontWeight: '600',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}>
                      <div style={{
                        fontSize: '18px',
                        marginBottom: '4px'
                      }}>
                        ❌
                      </div>
                      <div style={{ textAlign: 'center', lineHeight: '1.5' }}>
                        {intimateMessage.replace('❌', '').trim()}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: 'rgba(255, 255, 255, 0.8)',
                        marginTop: '4px'
                      }}>
                        此消息将在几秒后自动关闭
                      </div>
                    </div>
                  ) : (
                    // 正常生成状态
                    <>
                      <div style={{
                        color: 'white',
                        fontSize: '15px',
                        fontWeight: '600',
                        marginBottom: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}>
                        <span style={{ fontSize: '18px' }}>💝</span>
                        <span>AI伙伴正在练习亲密动作中...</span>
                      </div>
                      
                      {/* 进度条 */}
                      <div style={{
                        width: '100%',
                        height: '6px',
                        background: 'rgba(255, 255, 255, 0.2)',
                        borderRadius: '3px',
                        overflow: 'hidden',
                        marginBottom: rotatingVideos.length > 0 && currentVideo ? '10px' : '0'
                      }}>
                        <div style={{
                          width: `${intimateProgress}%`,
                          height: '100%',
                          background: 'linear-gradient(90deg, #4CAF50, #8BC34A)',
                          transition: 'width 0.3s ease',
                          borderRadius: '3px'
                        }}></div>
                      </div>
                      
                      {/* 显示当前轮播的动作 */}
                      {rotatingVideos.length > 0 && currentVideo && (
                        <div style={{
                          color: 'rgba(255, 255, 255, 1)',
                          fontSize: '14px',
                          fontWeight: '700',
                          textAlign: 'center',
                          textShadow: '0 2px 8px rgba(0, 0, 0, 0.4)'
                        }}>
                          💫 <span style={{ color: '#FFD700' }}>我正在为你表演{currentVideo.interaction_label || '热门动作'}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })()}

            {/* 专属动作完成提示条 */}
            {completedIntimateAction && !isGeneratingIntimate && (
              <div style={{
                position: 'absolute',
                top: '80px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'rgba(40, 40, 50, 0.7)',
                backdropFilter: 'blur(15px)',
                borderRadius: '16px',
                padding: '16px 24px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                zIndex: 15,
                minWidth: '320px',
                maxWidth: '90%',
                border: '1px solid rgba(255, 255, 255, 0.15)'
              }}>
                <div style={{
                  color: 'white',
                  fontSize: '15px',
                  fontWeight: '700',
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  textShadow: '0 2px 8px rgba(0, 0, 0, 0.4)'
                }}>
                  <span style={{ fontSize: '18px' }}>💝</span>
                  <span>
                    我为你表演的专属动作：
                    <span style={{ 
                      color: '#FFD700',
                      fontWeight: '800',
                      marginLeft: '6px'
                    }}>
                      {completedIntimateAction.label}
                    </span>
                  </span>
                </div>
              </div>
            )}

            {/* 左侧互动动作展示区 */}
            <div className="left-interact-actions">
              {(() => {
                const streamStatus = getStreamStatus();
                
                // 如果是休息状态，不显示互动动作
                if (streamStatus.isResting) {
                  return null;
                }
                
                // 获取可用的互动动作，最多显示5个
                const availableInteractions = videos
                  .filter(video =>
                    video.interaction_type &&
                    video.interaction_label &&
                    !FEED_SPECIAL_CATEGORIES.includes(video.video_category)
                  )
                  .slice(0, 5)
                  .map(video => ({
                    id: video.id,
                    type: video.interaction_type,
                    label: video.interaction_label,
                    emoji: video.emoji || '🎭'
                  }));
                
                if (availableInteractions.length === 0) {
                  return null;
                }
                
                return availableInteractions.map((interaction) => (
                  <div
                    key={interaction.id}
                    className="left-interact-item"
                  >
                    <span className="left-interact-emoji">{interaction.emoji}</span>
                    <span className="left-interact-label">{interaction.label}</span>
                  </div>
                ));
              })()}
            </div>

            <div className="stream-page-media-root">
            {(() => {
              const streamStatus = getStreamStatus();
              
              // 如果是休息状态，显示角色图片
              if (streamStatus.isResting) {
                return (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(45deg, #667eea, #764ba2)',
                    borderRadius: '0',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {(stream.character_image_url || stream.character_image) ? (
                      <img
                        src={stream.character_image_url || getImageUrl(stream.character_image)}
                        alt="角色图片"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          objectPosition: 'center'
                        }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div style={{
                        color: 'white',
                        fontSize: '18px',
                        textAlign: 'center',
                        padding: '20px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%'
                      }}>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>😴</div>
                        <div>AI伙伴正在休息中</div>
                      </div>
                    )}
                  </div>
                );
              }
              
              // 如果是直播状态，按原逻辑显示
              if (isShowingInitial) {
                // 显示初始画面
                return (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(45deg, #667eea, #764ba2)',
                    borderRadius: '0',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {getInitialImageUrl() ? (
                      <img 
                        src={getInitialImageUrl()} 
                        alt="初始画面" 
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover', // 使用cover填充整个容器，避免黑边
                          objectPosition: 'center'
                        }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div style={{
                        color: 'white',
                        fontSize: '18px',
                        textAlign: 'center',
                        padding: '20px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%'
                      }}>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎭</div>
                        <div>使用右侧按钮开始互动</div>
                      </div>
                    )}
                  </div>
                );
              } else if (currentVideo) {
                // 如果有当前视频，显示视频播放器
                return (
                  <div 
                    style={{ position: 'relative', width: '100%', height: '100%', background: 'black' }}
                    onMouseMove={handleDragMove}
                    onMouseUp={() => {
                      // 全局鼠标抬起时重置拖拽状态
                      if (dragState.isDragging) {
                        const hotspot = hotspots.find(h => h.id === dragState.hotspotId);
                        if (hotspot) {
                          handleDragEnd({ clientX: dragState.currentX, clientY: dragState.currentY }, hotspot);
                        }
                      }
                    }}
                  >
                    {/* 音频状态指示器 */}
                    {effectiveMuted && (
                      <div style={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        background: 'rgba(255, 100, 100, 0.8)',
                        color: 'white',
                        padding: '8px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '600',
                        zIndex: 10,
                        cursor: 'pointer',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 100, 100, 0.3)'
                      }}
                      onClick={enableAudioPlayback}
                      title="尝试恢复有声播放"
                      >
                        {isAutoplayFallbackMuted ? '🔇 自动播放已临时静音' : '🔇 当前已静音播放'}
                      </div>
                    )}

                    {/* 单视频播放器（简化版） */}
                    <>
                      {currentPosterUrl && (
                        <img
                          className={`video-poster-layer ${showPosterWhileLoading && !isVideoReady ? '' : 'is-hidden'}`}
                          src={currentPosterUrl}
                          alt={`${stream?.name || '直播间'} 首帧图`}
                        />
                      )}
                      <video
                        key={currentVideo ? `${currentVideo.id}-${currentVideo.filename}` : 'none'}
                        ref={singleVideoRef}
                        className={`video-player ${isVideoReady ? 'is-visible' : ''}`}
                        src={currentVideoSrc || ''}
                        poster={showPosterWhileLoading && currentPosterUrl ? currentPosterUrl : undefined}
                        muted={effectiveMuted}
                        playsInline
                        preload="auto"
                        disablePictureInPicture
                        onLoadedData={() => setIsVideoReady(true)}
                        onCanPlay={() => {}}
                        onWaiting={() => {}}
                        onPlay={() => {}}
                        onPlaying={() => {
                          setIsVideoReady(true);
                          setShowPosterWhileLoading(false);
                        }}
                        onPause={() => {}}
                        onError={() => {
                          setIsVideoReady(false);
                          setShowPosterWhileLoading(true);
                        }}
                        onEnded={() => {
                          if (isGeneratingIntimate && rotatingVideos.length > 0) {
                            const nextIndex = (currentRotatingIndex + 1) % rotatingVideos.length;
                            setCurrentRotatingIndex(nextIndex);
                            setCurrentVideo(rotatingVideos[nextIndex]);
                            setPlaybackPhase('action');
                            setShowPosterWhileLoading(true);
                            return;
                          }

                          if (playbackPhase === 'intro' || currentVideo?.video_category === 'intro') {
                            if (baseVideo) {
                              setCurrentVideo(baseVideo);
                              setPlaybackPhase('idle');
                              setShowPosterWhileLoading(true);
                            }
                            return;
                          }

                          if (currentVideo && currentVideo.video_category === 'private') {
                            restartLoopAlignment();
                            return;
                          }

                          if (
                            currentVideo && baseVideo &&
                            (currentVideo.id === baseVideo.id || currentVideo.video_category === 'idle')
                          ) {
                            restartLoopAlignment();
                            setPlaybackPhase('idle');
                            return;
                          }

                          if (baseVideo) {
                            setCurrentVideo(baseVideo);
                            setPlaybackPhase('idle');
                            setShowPosterWhileLoading(true);
                          }
                        }}
                        style={{
                          position: 'absolute',
                          inset: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          objectPosition: 'center',
                          backgroundColor: 'black'
                        }}
                      />
                    </>

                    {isRealtimeGenerating && (
                      <div className="realtime-gen-overlay stream-realtime-overlay">
                        <div className="realtime-gen-indicator">
                          <div className="gen-spinner" />
                          <span className="gen-status-text">{realtimeGenMessage || '生成中...'}</span>
                        </div>
                      </div>
                    )}

                    {/* 热点映射层 */}
                    {showHotspots && hotspots.length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        pointerEvents: 'none',
                        zIndex: 10
                      }}>
                        {/* 热点区域 - 支持单击、双击、长按、拖拽 */}
                        {hotspots.map(hotspot => (
                          <div
                            key={hotspot.id}
                            onMouseDown={(e) => handleHotspotMouseDown(e, hotspot)}
                            onMouseMove={(e) => {
                              if (dragState.isDragging && dragState.hotspotId === hotspot.id) {
                                handleDragMove(e);
                              }
                            }}
                            onMouseUp={(e) => handleHotspotMouseUp(e, hotspot)}
                            onMouseLeave={handleHotspotMouseLeave}
                            onClick={(e) => {
                              // 阻止点击事件冒泡（已在 onMouseUp 处理）
                              e.stopPropagation();
                              e.preventDefault();
                            }}
                            style={{
                              position: 'absolute',
                              left: `${hotspot.position.x}%`,
                              top: `${hotspot.position.y}%`,
                              width: `${hotspot.position.width}%`,
                              height: `${hotspot.position.height}%`,
                              cursor: 'pointer',
                              background: 'transparent',
                              border: 'none',
                              pointerEvents: 'auto',
                              userSelect: 'none'
                            }}
                          >
                            {/* 所有视觉提示已移除 */}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              } else {
                // 如果没有视频，显示提示信息
                return (
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '18px',
                    background: 'linear-gradient(45deg, #667eea, #764ba2)'
                  }}>
                    使用右侧按钮开始互动
                  </div>
                );
              }
            })()}
            </div>

            {/* 右侧操作栏 */}
            <div className="right-action-bar">
              {/* 头像 */}
              <div className="action-item avatar-item">
                <div className="avatar-circle">
                  {(stream?.character_image_url || stream?.character_image) ? (
                    <img
                      src={stream.character_image_url || getImageUrl(stream.character_image)}
                      alt="avatar"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(135deg, #667eea, #764ba2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '20px'
                    }}>
                      🤖
                    </div>
                  )}
                </div>
                {!isFollowing && (
                  <button 
                    className="follow-plus"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFollow();
                    }}
                  >
                    +
                  </button>
                )}
              </div>
              
              {/* 点赞 */}
              <div 
                className="action-item" 
                onClick={(e) => {
                  e.stopPropagation();
                  handleLike();
                }}
              >
                <div className="action-icon">❤️</div>
                <div className="action-count">{formatCount(likeCount)}</div>
              </div>
              
              {/* 评论 */}
              <div className="action-item">
                <div className="action-icon">💬</div>
                <div className="action-count">0</div>
              </div>
              
              {/* 收藏 */}
              <div className="action-item">
                <div className="action-icon">⭐</div>
              </div>
              
              {/* 火团按钮 - 触发互动 */}
              <div 
                className={`action-item fire-button ${showInteractPanel ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowInteractPanel(!showInteractPanel);
                }}
              >
                <div className="fire-icon">🔥</div>
                <div className="fire-glow"></div>
              </div>
            </div>
          </div>

          {/* 右侧面板 */}
          <div className="interaction-panel">
            {/* 1. 直播间信息区域 - 已移至视频顶部 */}

            {/* 2. 互动方式区域 */}
            <div className="interaction-sections">
              {(() => {
                const streamStatus = getStreamStatus();
                
                // 如果是休息状态，显示休息提示
                if (streamStatus.isResting) {
                  return (
                    <div className="resting-mode">
                      <div className="resting-icon">😴</div>
                      <div className="resting-text">AI伙伴正在休息中</div>
                      <div className="resting-subtext">
                        暂时无法互动，请稍后再来
                      </div>
                    </div>
                  );
                }
                
                const specialInteractions = videos
                  .filter(video =>
                    video.interaction_type &&
                    video.interaction_label &&
                    !FEED_SPECIAL_CATEGORIES.includes(video.video_category)
                  )
                  .map(video => ({
                    id: video.id,
                    type: video.interaction_type,
                    label: video.interaction_label,
                    emoji: video.emoji || '🎭', // 使用AI生成的emoji，如果没有则使用默认emoji
                    isCandidate: false
                  }));
                
                if (specialInteractions.length > 0) {
                  return (
                    <div className="interaction-group">
                      <div className="group-title">快来跟我互动吧</div>
                      <div className="interaction-buttons">
                        {specialInteractions.map((interaction) => (
                          <button
                            key={interaction.id}
                            className={`interaction-btn special ${activeButton === interaction.type ? 'active' : ''}`}
                            onClick={() => handleInteraction(interaction.id)}
                            title={`点击${interaction.label}`}
                          >
                            <span className="btn-emoji">{interaction.emoji}</span>
                            <span className="btn-label">{interaction.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                } else {
                  return (
                    <div className="no-interactions">
                      <div className="no-interactions-icon">📹</div>
                      <div className="no-interactions-text">暂无互动内容</div>
                      <div className="no-interactions-subtext">
                        该互动空间还没有配置互动视频
                      </div>
                    </div>
                  );
                }
              })()}
            </div>

            {/* 3. 打赏区域 */}

            {/* 4. 聊天室区域 - 已移除 */}

            {/* 5. 视频音频控制区域 - 已移动到右侧设置面板 */}


            {/* 6. 背景音乐控制区域 - 已移动到右侧设置面板 */}


            {/* 7. 互动统计信息区域 - 已移除 */}

          </div>
        </div>

        {/* 底部输入区 */}
        <div className="bottom-interact-bar">
          <input
            type="text"
            placeholder="说点什么..."
            className="interact-input"
          />
        </div>

        {/* 底部互动弹出面板 - 使用独立组件 */}
        <InteractPanel
          isOpen={showInteractPanel}
          onClose={() => setShowInteractPanel(false)}
          videos={videos}
          handleInteraction={handleInteraction}
          getStreamStatus={getStreamStatus}
          activeButton={activeButton}
          streamId={id}
          onIntimateGenerationStart={handleIntimateGenerationStart}
        />

        </div>
      </div>
      
      {/* 隐藏的背景音乐音频元素 */}
      {currentMusic && (
        <audio
          ref={musicAudioRef}
          src={currentMusic.audio_url}
          loop
          preload="metadata"
          style={{ display: 'none' }}
          onLoadedMetadata={() => { if (musicAudioRef.current) musicAudioRef.current.volume = musicVolume / 100; }}
        />
      )}
    </div>
  );
};

export default StreamPage;
